import React, { useEffect, useRef, useState } from "react";
import { axiosMaster } from "../../Components/api/axiosInstance";

const inputClass =
  "w-full border rounded-md px-2 py-1.5 focus:ring-1 focus:ring-blue-500 text-sm";

const toTitleCase = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const AddEditRestaurantMaster = ({
  editingData,
  onClose,
  onSuccess,
  groupId,
  groupName,
}) => {
  const [form, setForm] = useState({
    id: "",
    restId: 0,
    restaurantName: "",
    brandName: groupName || "",
    contactPersonName: "",
    contactPersonMobile: "",
    contactPersonEmail: "",
    biometricId: "",
    maxEmployees: 0,
    status: true,
    isRoundTheClock: false,
    openingTime: "",
    closingTime: "",
    maxWorkingMinutes: "",
  });

  const [saving, setSaving] = useState(false);

  // ✅ USER FROM LOCALSTORAGE
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = storedUser?.userId || 0;

  // refs
  const nameRef = useRef(null);
  const brandRef = useRef(null);
  const contactRef = useRef(null);
  const mobileRef = useRef(null);
  const emailRef = useRef(null);
  const biometricRef = useRef(null);
  const maxEmpRef = useRef(null);
  const statusRef = useRef(null);
  const openingRef = useRef(null);
  const closingRef = useRef(null);
  const workingMinRef = useRef(null);
  const saveRef = useRef(null);

  // Load edit data
  useEffect(() => {
    if (editingData) {
      setForm((prev) => ({
        ...prev,
        ...editingData,
      }));
    }
  }, [editingData]);

  // Autofocus
  useEffect(() => {
    setTimeout(() => {
      nameRef.current?.focus();
    }, 100);
  }, []);

  // Global keys
  useEffect(() => {
    const handleGlobalKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter" && e.ctrlKey) handleSubmit();
    };
    window.addEventListener("keydown", handleGlobalKey);
    return () => window.removeEventListener("keydown", handleGlobalKey);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "contactPersonMobile" && value.length > 10) return;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleKeyDown = (e, nextRef, prevRef, isLast = false) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef?.current) nextRef.current.focus();
      else if (isLast) handleSubmit();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      prevRef?.current?.focus() || onClose?.();
    }
  };

  const handleBlurFormat = (e) => {
    const { name, value } = e.target;
    if (["restaurantName", "brandName", "contactPersonName"].includes(name)) {
      setForm((prev) => ({ ...prev, [name]: toTitleCase(value) }));
    }
  };

  const handleSubmit = async () => {
    if (!form.restaurantName.trim()) {
      alert("Restaurant Name required");
      return;
    }

    if (!form.biometricId.trim()) {
      alert("Biometric ID required");
      return;
    }

    if (form.contactPersonMobile && form.contactPersonMobile.length !== 10) {
      alert("Mobile must be 10 digits");
      return;
    }

    if (form.contactPersonEmail && !isValidEmail(form.contactPersonEmail)) {
      alert("Invalid email");
      return;
    }

    if (!form.isRoundTheClock) {
      if (!form.openingTime || !form.closingTime) {
        alert("Opening & Closing time required");
        return;
      }
    }

    if (form.isRoundTheClock && !form.maxWorkingMinutes) {
      alert("Max Working Minutes required");
      return;
    }

    const payload = {
      ...form,
      groupId,
      openingTime: form.isRoundTheClock ? null : form.openingTime,
      closingTime: form.isRoundTheClock ? null : form.closingTime,
      maxWorkingMinutes: form.isRoundTheClock
        ? Number(form.maxWorkingMinutes)
        : null,
      createdBy: userId,
      modifiedBy: userId,
      userId: userId,
      entryDate: new Date().toISOString(),
      lastModifyDate: new Date().toISOString(),
    };

    console.log("PAYLOAD:", payload);

    try {
      setSaving(true);
      await axiosMaster.post(
        "/RestaurantMaster/UpsertRestaurant",
        payload
      );
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999]"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 border text-sm"
        onClick={(e) => e.stopPropagation()}
      >
      <h2 className="font-semibold mb-4 text-base">
  {form.id ? "Edit Restaurant" : "Add Restaurant"}
</h2>

<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

  {/* Restaurant Name */}
  <div>
    <label className="text-xs text-gray-600">Restaurant Name *</label>
    <input
      ref={nameRef}
      name="restaurantName"
      value={form.restaurantName}
      onChange={handleChange}
      onBlur={handleBlurFormat}
      onKeyDown={(e) => handleKeyDown(e, brandRef)}
      className={inputClass}
    />
  </div>

  {/* Brand */}
  <div>
    <label className="text-xs text-gray-600">Brand Name</label>
    <input
      ref={brandRef}
      name="brandName"
      value={form.brandName}
      onChange={handleChange}
      onBlur={handleBlurFormat}
      onKeyDown={(e) => handleKeyDown(e, contactRef, nameRef)}
      className={inputClass}
    />
  </div>

  {/* Contact */}
  <div>
    <label className="text-xs text-gray-600">Contact Person</label>
    <input
      ref={contactRef}
      name="contactPersonName"
      value={form.contactPersonName}
      onChange={handleChange}
      onBlur={handleBlurFormat}
      onKeyDown={(e) => handleKeyDown(e, mobileRef, brandRef)}
      className={inputClass}
    />
  </div>

  {/* Mobile */}
  <div>
    <label className="text-xs text-gray-600">Mobile</label>
    <input
      ref={mobileRef}
      name="contactPersonMobile"
      value={form.contactPersonMobile}
      onChange={handleChange}
      onKeyDown={(e) => handleKeyDown(e, emailRef, contactRef)}
      className={inputClass}
    />
  </div>

  {/* Email */}
  <div>
    <label className="text-xs text-gray-600">Email</label>
    <input
      ref={emailRef}
      name="contactPersonEmail"
      value={form.contactPersonEmail}
      onChange={handleChange}
      onKeyDown={(e) => handleKeyDown(e, biometricRef, mobileRef)}
      className={inputClass}
    />
  </div>

  {/* Biometric */}
  <div>
    <label className="text-xs text-gray-600">Biometric ID</label>
    <input
      ref={biometricRef}
      name="biometricId"
      value={form.biometricId}
      onChange={handleChange}
      onKeyDown={(e) => handleKeyDown(e, maxEmpRef, emailRef)}
      className={inputClass}
    />
  </div>

  {/* Max Employees */}
  <div>
    <label className="text-xs text-gray-600">Max Employees</label>
    <input
      ref={maxEmpRef}
      type="number"
      name="maxEmployees"
      value={form.maxEmployees}
      onChange={handleChange}
      onKeyDown={(e) => handleKeyDown(e, statusRef, biometricRef)}
      className={inputClass}
    />
  </div>

  {/* Status */}
  <div className="flex items-end gap-2 pb-1">
    <input
      ref={statusRef}
      type="checkbox"
      name="status"
      checked={form.status}
      onChange={handleChange}
    />
    <label className="text-sm text-gray-700">Active</label>
  </div>
</div>

{/* TIME SECTION */}
<div className="border-t mt-4 pt-3">
  <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
    <input
      type="checkbox"
      name="isRoundTheClock"
      checked={form.isRoundTheClock}
      onChange={handleChange}
    />
    24 Hours Open
  </label>

  {!form.isRoundTheClock ? (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="text-xs text-gray-600">Opening Time</label>
        <input
          ref={openingRef}
          type="time"
          name="openingTime"
          value={form.openingTime}
          onChange={handleChange}
          onKeyDown={(e) =>
            handleKeyDown(e, closingRef, statusRef)
          }
          className={inputClass}
        />
      </div>

      <div>
        <label className="text-xs text-gray-600">Closing Time</label>
        <input
          ref={closingRef}
          type="time"
          name="closingTime"
          value={form.closingTime}
          onChange={handleChange}
          onKeyDown={(e) =>
            handleKeyDown(e, saveRef, openingRef, true)
          }
          className={inputClass}
        />
      </div>
    </div>
  ) : (
    <div>
      <label className="text-xs text-gray-600">
        Max Working Minutes
      </label>
      <input
        ref={workingMinRef}
        type="number"
        name="maxWorkingMinutes"
        value={form.maxWorkingMinutes}
        onChange={handleChange}
        onKeyDown={(e) =>
          handleKeyDown(e, saveRef, statusRef, true)
        }
        className={inputClass}
      />
    </div>
  )}
</div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-gray-500 text-white rounded"
          >
            Cancel
          </button>

          <button
            ref={saveRef}
            onClick={handleSubmit}
            disabled={saving}
            className="px-3 py-1.5 bg-blue-600 text-white rounded"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditRestaurantMaster;